/**
 * Where Pollie Report runs — from the current route (and IQ Hub tab when present).
 */

function stateFromRoute(route) {
  const code = (route?.params?.code || '').trim().toUpperCase();
  return code.length === 2 ? code : '';
}

function normalizeStateCode(code) {
  const c = (code || '').trim().toUpperCase();
  return c.length === 2 ? c : '';
}

function isIqHubLandscapeTab(route) {
  const tab = (route?.query?.tab || '').toString().toLowerCase();
  return tab === 'landscape';
}

function landscapeContext(stateCode) {
  const stateBit = stateCode ? ` in ${stateCode}` : '';
  return {
    report_context: 'landscape',
    report_state: stateCode,
    scopeLine: stateCode
      ? `Reports use Landscape data for ${stateCode}.`
      : 'Reports use Landscape data.',
    hint: `Say what you want in plain English. Example: counties${stateBit} still missing provider counts. Print when the list looks right.`,
    placeholder: stateCode
      ? `Example: counties in ${stateCode} still missing provider counts`
      : 'Example: counties still missing provider counts',
  };
}

function iqHubBriefContext() {
  return {
    report_context: 'iq_hub',
    report_state: '',
    scopeLine: 'Reports use My AccessIQ — brief, signals, and updates on this hub.',
    hint: 'Say what you want in plain English. Example: updates in this week\'s brief that mention rate changes. Print when the list looks right.',
    placeholder: 'Example: rate changes mentioned in this week\'s brief',
  };
}

function stateIqContext(hubState) {
  const stateBit = hubState ? ` in ${hubState}` : '';
  return {
    report_context: 'state_iq',
    report_state: hubState,
    scopeLine: hubState
      ? `Reports use State IQ — legislation and bills for ${hubState}.`
      : 'Reports use State IQ — legislation for your anchor states and interests.',
    hint: `Say what you want in plain English. Example: bills${stateBit} that match my org interests this session. Print when the list looks right.`,
    placeholder: hubState
      ? `Example: bills in ${hubState} that match my interests`
      : 'Example: bills in my anchor states that match my interests',
  };
}

function globalIqContext() {
  return {
    report_context: 'global_iq',
    report_state: '',
    scopeLine: 'Reports use Global IQ — converging signals and macro trends.',
    hint: 'Say what you want in plain English. Example: converging signals about workforce from the last 30 days. Print when the list looks right.',
    placeholder: 'Example: converging signals about workforce from the last 30 days',
  };
}

function whatIfContext() {
  return {
    report_context: 'what_if_iq',
    report_state: '',
    scopeLine: 'Reports use What-If IQ — scenario branches and risks.',
    hint: 'Say what you want in plain English. Example: active scenarios about supply chain disruption. Print when the list looks right.',
    placeholder: 'Example: active scenarios about supply chain disruption',
  };
}

function docketContext(hubState) {
  const stateBit = hubState ? ` in ${hubState}` : '';
  return {
    report_context: 'docket_iq',
    report_state: hubState,
    scopeLine: hubState
      ? `Reports use Docket IQ — hearings and meetings for ${hubState}.`
      : 'Reports use Docket IQ — hearings and legislative meetings.',
    hint: `Say what you want in plain English. Example: upcoming committee hearings${stateBit} this month. Print when the list looks right.`,
    placeholder: hubState
      ? `Example: upcoming hearings in ${hubState} this month`
      : 'Example: upcoming committee hearings this month',
  };
}

function briefingContext(hubState) {
  const stateBit = hubState ? ` for ${hubState}` : '';
  return {
    report_context: 'briefing',
    report_state: hubState,
    scopeLine: 'Reports use Briefing — saved stories from your daily brief.',
    hint: `Say what you want in plain English. Example: stories saved from this month's daily brief${stateBit}. Print when the list looks right.`,
    placeholder: 'Example: stories saved from this month\'s daily brief',
  };
}

function polliePacketsContext(hubState) {
  return {
    report_context: 'pollie_packets',
    report_state: hubState,
    scopeLine: hubState
      ? `Reports use Pollie Packets saved for ${hubState}.`
      : 'Reports use your saved Pollie Packets.',
    hint: 'Say what you want in plain English. Example: complete Pollie Packets from this year. Print when the list looks right.',
    placeholder: 'Example: complete Pollie Packets from this year',
  };
}

function policyContext(hubState) {
  const stateBit = hubState ? ` for ${hubState}` : '';
  return {
    report_context: 'policy',
    report_state: hubState,
    scopeLine: 'Reports use Policy Engine — knowledge library and sources.',
    hint: `Say what you want in plain English. Example: knowledge library items added this month${stateBit}. Print when the list looks right.`,
    placeholder: 'Example: knowledge library items added this month',
  };
}

/**
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {{ hubState?: string }} [options]
 */
export function resolvePollieReportContext(route, options = {}) {
  const name = route?.name || '';
  const path = route?.path || '';
  const hubState = normalizeStateCode(options.hubState);
  const routeState = stateFromRoute(route);
  const stateCode = hubState || routeState;

  if (
    name === 'IwiQueueManager'
    || name === 'FeedbackDashboard'
    || path.startsWith('/internal/feedback')
  ) {
    return {
      report_context: 'iwi',
      report_state: '',
      scopeLine: 'Reports use your I Wonder If cards.',
      hint: 'Say what you want in plain English. Example: cards in building with no target date. Print when the list looks right.',
      placeholder: 'Example: cards in building with no target date',
    };
  }

  if (path.startsWith('/iq-hub') || String(name).startsWith('IQHub')) {
    if (isIqHubLandscapeTab(route)) {
      return landscapeContext(stateCode);
    }
    return iqHubBriefContext();
  }

  if (path.startsWith('/state-build') || String(name).startsWith('StateBuild')) {
    return landscapeContext(stateCode);
  }

  if (path.startsWith('/state-iq') || name === 'StateIQ' || name === 'StateIQPrint') {
    return stateIqContext(stateCode);
  }

  if (path.startsWith('/converging-signals')) {
    return globalIqContext();
  }

  if (path.startsWith('/what-if-iq')) {
    return whatIfContext();
  }

  if (path.startsWith('/docket-iq')) {
    return docketContext(stateCode);
  }

  if (path.startsWith('/briefings')) {
    return briefingContext(stateCode);
  }

  if (path.startsWith('/pollie-packets')) {
    return polliePacketsContext(stateCode);
  }

  if (path.startsWith('/policy-engine') || name === 'PolicyEngine') {
    return policyContext(stateCode);
  }

  return {
    report_context: 'general',
    report_state: '',
    scopeLine: 'Reports use data for the page you are on.',
    hint: 'Say what you want in plain English about what you see on this page. Print when the list looks right.',
    placeholder: 'Describe the report you want',
  };
}

/** Backend fallback when the client did not send report_context. */
export function inferReportContextFromPath(pageRoute) {
  const raw = pageRoute || '';
  const [path, query = ''] = raw.split('?');
  const params = new URLSearchParams(query);
  if (path.startsWith('/internal/feedback')) return 'iwi';
  if (path.startsWith('/iq-hub')) {
    return params.get('tab') === 'landscape' ? 'landscape' : 'iq_hub';
  }
  if (path.startsWith('/state-build')) return 'landscape';
  if (path.startsWith('/policy-engine')) return 'policy';
  if (path.startsWith('/state-iq')) return 'state_iq';
  if (path.startsWith('/converging-signals')) return 'global_iq';
  if (path.startsWith('/what-if-iq')) return 'what_if_iq';
  if (path.startsWith('/docket-iq')) return 'docket_iq';
  if (path.startsWith('/briefings')) return 'briefing';
  if (path.startsWith('/pollie-packets')) return 'pollie_packets';
  return 'general';
}
